import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetBillingProfile } from 'features/subscription/hooks/use-subscription';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { CREDITS_ERROR } from '../constants/credits';
import { creditsErrorCode } from '../services/credits-service';
import type { CreditSummary } from '../types';
import { toMinor } from '../utils/amount';
import { usableCards } from '../utils/cards';
import { couponErrorTexts } from '../utils/coupon-errors';
import { useCreateTopUp, useSavedCards } from './use-credits';

/**
 * Paying for a top-up: on the saved card with no redirect, or on the hosted checkout page. Asks for
 * billing details first when the invoice needs them. Shared by the Credits tab and Buy credits.
 */
export const useTopUpPayment = (summary: CreditSummary, amountChf: number | null, couponCode?: string) => {
  const { t } = useTranslation();
  const [chargeId, setChargeId] = useState<string>();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [billingRefused, setBillingRefused] = useState(false);
  // The card the payment was going to use, kept while the billing dialog is open.
  const [billingFor, setBillingFor] = useState<{ paymentMethodId?: string }>();
  const cards = useSavedCards();
  const profile = useGetBillingProfile();
  const { mutate: createTopUp, isPending, variables, error, reset } = useCreateTopUp();

  const card = usableCards(cards.data)[0];
  const isChecking = cards.isLoading || profile.isLoading;
  const needsBilling =
    billingRefused || (!profile.isLoading && !profile.isError && !profile.data?.isComplete);
  const isChargingCard = isPending && !!variables?.paymentMethodId;
  const errorCode = creditsErrorCode(error);

  useEffect(() => {
    const leaveRedirect = (event: PageTransitionEvent) => event.persisted && setIsRedirecting(false);
    window.addEventListener('pageshow', leaveRedirect);
    return () => window.removeEventListener('pageshow', leaveRedirect);
  }, []);

  const start = (paymentMethodId?: string, code = couponCode) => {
    if (amountChf === null) return;
    reset();
    createTopUp(
      { amountChf, paymentMethodId, couponCode: code },
      {
        onSuccess: ({ topUpId, checkoutUrl }) => {
          if (!checkoutUrl) {
            setChargeId(topUpId);
            return;
          }
          setIsRedirecting(true);
          window.location.href = checkoutUrl;
        },
        onError: (failure) => {
          if (creditsErrorCode(failure) !== CREDITS_ERROR.billingIncomplete) return;
          setBillingRefused(true);
          setBillingFor({ paymentMethodId });
        },
      }
    );
  };

  const pay = (paymentMethodId?: string) => {
    if (needsBilling) {
      setBillingFor({ paymentMethodId });
      return;
    }
    start(paymentMethodId);
  };

  const handleBillingSaved = () => {
    const paymentMethodId = billingFor?.paymentMethodId;
    setBillingRefused(false);
    setBillingFor(undefined);
    start(paymentMethodId);
  };

  const errorText: Record<string, string> = {
    [CREDITS_ERROR.amountTooSmall]: t('AMOUNT_MINIMUM', {
      amount: formatMinor(toMinor(summary.minTopUpChf), summary.currency),
      defaultValue: 'The minimum top-up is {{amount}}.',
    }),
    [CREDITS_ERROR.amountTooLarge]: t('AMOUNT_MAXIMUM', {
      amount: formatMinor(toMinor(summary.maxTopUpChf), summary.currency),
      defaultValue: 'The maximum top-up is {{amount}}.',
    }),
    ...couponErrorTexts(t),
    [CREDITS_ERROR.cardDeclined]: t(
      'CARD_DECLINED_FULL',
      'Your bank declined this charge. Try another card.'
    ),
    [CREDITS_ERROR.providerMissing]: t(
      'PAYMENT_PROVIDER_MISSING',
      'Payments are not set up for this account yet. Contact support.'
    ),
    [CREDITS_ERROR.paymentForbidden]: t(
      'PAYMENT_FORBIDDEN',
      'Your account is not allowed to make payments. Ask an administrator.'
    ),
    [CREDITS_ERROR.paymentUnavailable]: t(
      'PAYMENT_UNAVAILABLE',
      'The payment service is unavailable. Try again in a moment.'
    ),
  };

  return {
    card,
    isChecking,
    isPending,
    isChargingCard,
    isRedirecting,
    chargeId,
    clearCharge: () => setChargeId(undefined),
    hasFailed: !!error && errorCode !== CREDITS_ERROR.billingIncomplete,
    failureText: errorText[errorCode] ?? t('TOPUP_START_FAILED', 'The top-up could not be started. Try again.'),
    start,
    pay,
    billing: {
      isOpen: !!billingFor,
      close: () => setBillingFor(undefined),
      onSaved: handleBillingSaved,
    },
  };
};
